// An independent professional's kind of work (backend/work_kinds.py): the founder sets it in the invite and on Team,
// and the professional sees it. Code gets the terminal view and git words; Legal & terms and Writing & translation get
// the documents view and plain words (design_handoff_documents_view). Nobody switches the view. A session keeps the
// kind its professional had when it was created.
export const WORK_KINDS = [
  ["code", "Code", "Terminal view"],
  ["legal", "Legal & terms", "Documents view"],
  ["writing", "Writing & translation", "Documents view"],
];
// Design and Data had no workspace of their own (owner, 2026-10-14): their people and open sessions moved to code;
// ended sessions and change reports keep the kind they had, with its label
const RETIRED = [["design", "Design", "Documents view"], ["data", "Data", "Documents view"]];
export const KIND_LABELS = Object.fromEntries([...WORK_KINDS, ...RETIRED].map(([k, label]) => [k, label]));
export const KIND_VIEWS = Object.fromEntries([...WORK_KINDS, ...RETIRED].map(([k, , view]) => [k, view]));
export const RESPONSIBILITY_HINTS = {
  code: "e.g. Frontend and API", legal: "e.g. Terms, privacy and refund policy",
  writing: "e.g. Help center and emails, English and German",
};

export const kindOf = (doc) => (KIND_LABELS[doc?.work_kind] ? doc.work_kind : "code");
export const isDocuments = (kind) => kind !== "code";

// A document's readable name: its file name without the extension, dashes and underscores as spaces, the first letter
// capitalized (terms-of-service.md is "Terms of service")
export function docName(path) {
  const file = (path || "").split("/").pop().replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
  return file ? file.charAt(0).toUpperCase() + file.slice(1) : path;
}
export const docFolder = (path) => (path || "").includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
export const isMarkdown = (path) => /\.(md|markdown)$/i.test(path || "");
// the folder line under a document's name: a file that isn't Markdown also says its type ("docs/legal · JSON file")
export function docFolderLine(path) {
  const folder = docFolder(path) || "Top folder";
  const ext = ((path || "").split("/").pop().match(/\.([^.]+)$/) || [])[1];
  return isMarkdown(path) || !ext ? folder : `${folder} · ${ext.toUpperCase()} file`;
}

// a file's state in documents words, from git's: NEW, CHANGED, REMOVED
export const DOC_STATE = { added: ["New", "info"], modified: ["Changed", "warning"], deleted: ["Removed", "danger"] };

// A version is a turn that saved something: every turn makes a commit ("ses 3f2a91 · turn 3 · 2 files +8 -4"), even
// one that changed nothing, and only those that changed files count. Versions are numbered from 1, oldest first, the
// same in the conversation, the side panel, the changes' tabs and the founder's report. Returns them oldest first,
// each with `n` (its version), `turn` and `files`.
const TURN = /turn (\d+) · (\d+) files?/;
export function versionsOf(commits) {
  return (commits || []).map((c) => {
    const m = TURN.exec(c.message || "");
    return { ...c, turn: m ? Number(m[1]) : null, files: c.files ?? (m ? Number(m[2]) : 0) };
  }).filter((c) => c.files > 0)
    .sort((a, b) => (a.turn ?? 0) - (b.turn ?? 0) || String(a.ts || a.at || "").localeCompare(String(b.ts || b.at || "")))
    .map((c, i) => ({ ...c, n: i + 1 }));
}

export const versionDetail = (v) => `${v.files} ${v.files === 1 ? "document" : "documents"} changed`;
